<template>
  <a
    :key="city.slug"
    v-view-tracker="() => viewParams"
    class="city city-around-page"
    lang="ru"
    :href="href"
    @click.prevent="sogu"
  >
    <img
      class="city__img"
      :src="city?.image?.thumbnail || ''"
      :alt="city.name_ru"
      loading="lazy"
    />
    <div
      v-if="isNearestCityExcursions"
      class="city__attractions"
      :data-count="`∙ ${city.attractions_count}`"
    >
      {{ city.name_ru }}
    </div>
    <div
      v-else
      class="city__info"
    >
      <div class="city__name">{{ city.name_ru }}</div>
      <div class="city__count">{{ city.excursions_count }}</div>
    </div>
  </a>
</template>

<script lang="ts" setup>
  import { ref, computed, onMounted } from 'vue';

  import { trackEvent, useViewTracker } from '@/services/analytics';
  import { type CityAround } from '@/shared/api/generated/api';
  import { useSightAnalyticsParams } from '@/shared/utils/analytics';

  const props = defineProps<{
    city: CityAround;
    isNearestCityExcursions?: boolean;
  }>();

  const analyticsParams = useSightAnalyticsParams();

  const link = ref<string | undefined>('');

  const viewParams = computed(() => ({
    ...analyticsParams.value,
    city_name: props.city.name_ru,
  }));

  const vViewTracker = useViewTracker('attractions:other-cities.view');

  const sogu = async () => {
    try {
      await trackEvent('attractions:other-cities.click', viewParams.value);
    } finally {
      window.location.href = href.value;
    }
  };

  // Computed properties
  const url = computed(() => {
    return props.isNearestCityExcursions ? `${props.city.url ?? ''}sights` : props.city.url;
  });

  const href = computed(() => `/attractions/${props.city.slug ?? ''}/`);

  // Watch on mounted
  onMounted(() => {
    const computedUrl: string = url.value || '';
    link.value = computedUrl;
  });
</script>

<style lang="scss" scoped>
  @use '@experience/design-tokens/typography' as legacy-typography;
  @use '@experience/shared/dist/assets/adaptivity' as legacy-adaptivity;

  .city {
    position: relative;
    box-sizing: border-box;
    aspect-ratio: 4 / 3;
    overflow: hidden;
    text-decoration: none;
    cursor: pointer;
    border-radius: var(--alias-radius-8);
    transform: translate3d(0, 0, 0);
    transition: all 0.2s;

    &::after {
      position: absolute;
      inset: 0;
      z-index: 1;
      pointer-events: none;
      content: '';
      background: linear-gradient(to bottom, transparent 70%, var(--alias-color-background-inverse) 100%);
      border-radius: var(--alias-radius-8);

      @include legacy-adaptivity.tablet {
        display: none;
      }
    }

    &:hover {
      .city__img {
        transform: scale(110%);
        transition: all 0.2s ease-out;

        @include legacy-adaptivity.tablet {
          padding: 4px 4px 0;
          transform: none;
        }
      }
    }

    .one-row & {
      padding: 0;
      background: none;
    }

    @include legacy-adaptivity.tablet {
      background: var(--alias-color-background-subtle);
    }

    @include legacy-adaptivity.tablet {
      width: 100%;
      aspect-ratio: auto;
      overflow: visible;
    }

    @include legacy-adaptivity.mobile {
      width: 100%;
    }
  }

  .city-around-page {
    position: relative;
    display: block;
    height: 100%;

    @include legacy-adaptivity.tablet {
      position: static;
      width: 100%;
      min-width: 176px;
      max-width: 100%;
      padding: 4px 4px 0;
      border-radius: 12px;

      .city__img {
        width: 100%;
        height: auto;
        aspect-ratio: 4 / 3;
      }
    }

    @include legacy-adaptivity.mobile {
      min-width: 176px;
      max-width: 176px;
    }
  }

  .city__img {
    display: block;
    width: 100%;
    height: 100%;
    border-radius: var(--alias-radius-8);
    opacity: 1;
    transition: all 0.2s ease-out;
  }

  .city__info {
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    padding: 0 12px 12px;
    font-size: 18px;
    color: var(--alias-color-text-inverse);

    @include legacy-adaptivity.tablet {
      position: initial;
      display: flex;
      flex: 1;
      align-items: center;
      padding: 0;
      margin-top: 8px;

      // color: var(--alias-color-text-inverse);
      text-align: center;

      @include legacy-typography.base-300-300-regular;
    }

    @include legacy-adaptivity.mobile {
      @include legacy-typography.base-400-300-regular;
    }
  }

  .city__name {
    display: -webkit-box;
    overflow: hidden;
    -webkit-line-clamp: 3;
    hyphens: auto;
    overflow-wrap: break-word;
    -webkit-box-orient: vertical;

    @include legacy-typography.base-400-300-semi;

    @include legacy-adaptivity.tablet {
      color: var(--alias-color-text-default);

      @include legacy-typography.base-300-300-medium;
    }

    @include legacy-adaptivity.mobile {
      @include legacy-typography.base-300-300-medium;
    }
  }

  .city__count {
    @include legacy-typography.base-200-200-medium;

    @include legacy-adaptivity.tablet {
      color: var(--alias-color-text-subtle);
    }
  }

  .city__attractions {
    position: absolute;
    right: 8px;
    bottom: 8px;
    left: 8px;
    z-index: 2;
    color: var(--alias-color-text-inverse);
    hyphens: auto;

    &::after {
      white-space: nowrap;
      content: ' ' attr(data-count);
    }

    @include legacy-typography.base-400-300-semi;

    @include legacy-adaptivity.tablet {
      position: static;
      margin: var(--alias-space-8, 8px) 0;
      color: var(--alias-color-overlay-modal);
      text-align: center;

      @include legacy-typography.base-300-300-semi;
    }
  }
</style>
